import { Injectable } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Observable, combineLatest, map, startWith } from 'rxjs';
import { ContribBiennaleWarningsService } from './warnings/contrib-biennale.warnings';
import { contribBiennaleFormValidators } from './validators/contrib-biennale.form-validators';
import { getControl } from '@shared/helpers/form.helper';
import { toNum } from '@shared/helpers/math.helper';
import { ContribBiennaleFormListeners } from './listeners/contrib-biennale.form-listeners';
import { BaseRevisioneFormService } from '../../abstracts/base-revisione-form.service';
import { MyVerbale } from '../../models/interfaces/revisione.interface';
import { DbCentraleEntity } from '../../models/interfaces/dati-business.interface';
import { buildLiquidatoriFormArray } from '../amm-controllo/common/helpers/norme-form-groups.helper';
import { buildContribAssociativoFormArray, buildContribRevPrecFormArray, buildVersamentoFormArray } from './common/builders/contrib-biennale-form-array.builder';
import { buildSocietaPartecipateFormArray } from '../dati-bilancio/common/builders/dati-bilancio-form-array.builder';
import { buildAderentiAFormArray } from '../dati-identificativi/common/builders/dati-identificativi-form-array.builder';
import { setInitialTipoSezEnte } from '../../helpers/form-store.helpers';
import { formPaths } from '@shared/constants/form-paths.const';
import { getContribBiennaleControl } from './validators/helpers/contrib-biennale-form.helpers';
import { Revisione53DatiBusinessSelectors } from '../../store/selectors/dati-business.selectors';

@Injectable()
export class ContribBiennaleFormService extends BaseRevisioneFormService<ContribBiennaleWarningsService> {
    constructor(protected warningsService: ContribBiennaleWarningsService) {
        super(warningsService);
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Calcolo contributo
            // my_moraHidden / my_sanzioneHidden / my_TotaleDovutoAllaData NON vanno qui: sono calcolati
            // e scritti solo da questa sezione. Registrarli come dependencyPaths della sezione stessa
            // causava un self-refresh (refreshFormData) ogni volta che la sezione li scriveva sullo
            // store, innescando una cascata che azzerava my_ContribVersato (vedi contrib-biennale-contributo.listeners.ts).
            'my_dataUltimaSincronizzazione',
            'my_dataCalcoloPenalita',
            'my_continuitaNelleRevisioni',
            'my_IdAssegnazione',
            'my_AllContributoAssociativo.my_AllElemContributoAssociativo',
            'my_AllContributoRevPrec.my_AllElemContributoRevPrec',
            'my_RTCalcoloMoraSanzione.my_RTRigaCalcoloMoraSanzione',

            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_Anno1`,
            `${formPaths.datiIdentificativi}.my_DenominazEnte`,
            `${formPaths.datiIdentificativi}.my_data_immatricolazione_cci`,
            `${formPaths.datiIdentificativi}.my_DataUltRev`,
            `${formPaths.datiIdentificativi}.my_DataRevisioneDal`,
            `${formPaths.datiIdentificativi}.my_SoggDispostoRev`,
            `${formPaths.datiIdentificativi}.my_CkMutualPrev`,
            `${formPaths.datiIdentificativi}.my_TipoSezEnte`,
            `${formPaths.datiIdentificativi}.my_DataCostituz`,
            `${formPaths.datiIdentificativi}.my_TipoIncarico`,
            `${formPaths.datiIdentificativi}.my_TipoIncaricoEffettivo`,
            `${formPaths.datiIdentificativi}.my_AderentiA.my_ElAderentiA`,

            // Bilancio
            `${formPaths.bilancio}.my_CkEnteTenutoCertBil`,
            `${formPaths.bilancio}.my_SocietaPartecipate.my_ElSocietaPartecipate`,
            `${formPaths.bilancio}.my_ContoEconomico.my_CoEcA12`,

            // Effettività base sociale
            `${formPaths.baseSociale}.my_ChiusuraEsercizio`,
            `${formPaths.baseSociale}.my_Data1EsPrec`,
            `${formPaths.baseSociale}.my_SociFisCoopPrimo`,
            `${formPaths.baseSociale}.my_SociGiuCoopPrimo`,
            `${formPaths.baseSociale}.my_SociFisFinPrimo`,
            `${formPaths.baseSociale}.my_SociGiuFinPrimo`,
            `${formPaths.baseSociale}.my_SociSpecPrimo`,
            `${formPaths.baseSociale}.my_Data2EsPrec`,
            `${formPaths.baseSociale}.my_SociFisCoopSecondo`,
            `${formPaths.baseSociale}.my_SociGiuCoopSecondo`,
            `${formPaths.baseSociale}.my_SociFisFinSecondo`,
            `${formPaths.baseSociale}.my_SociGiuFinSecondo`,
            `${formPaths.baseSociale}.my_SociSpecSecondo`,

            // Dati bilancio
            `${formPaths.datiBilancio}.my_BilAnno2`,
            `${formPaths.datiBilancio}.my_BilAnno1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAI1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAI2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAtot1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAtot2`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcAtot1`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcAtot2`,

            // Amministrazione controllo
            `${formPaths.ammControllo}.my_DataAssembleaLiquidazVolontaria`,
            `${formPaths.ammControllo}.my_AllLiquidatori.my_AllElemLiquidatori`,
            `${formPaths.ammControllo}.my_CkNorme`,
            `${formPaths.ammControllo}.my_NumLiquidatori`,
            `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlCoop9soci`,
            `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlCoop20soci`,
            `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlAffidamContrCont`,
            `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlTipoAmministrazione`,
            `${formPaths.ammControllo}.my_NormeSrl.my_SrlScadenzaAmministratori`,
            `${formPaths.ammControllo}.my_NormeSpaDualistico.my_CkSpdAffidamContrCont`,
            `${formPaths.ammControllo}.my_NormeSpaOrdinario.my_CkSpoAffidamContrCont`,
            `${formPaths.ammControllo}.my_NormeSpaOrdinario.my_CkSpoTipoAmministrazione`,
            `${formPaths.ammControllo}.my_NormeSpaMonistico.my_CkSpmAffidamContrCont`,
            `${formPaths.ammControllo}.my_NormeSpaMonistico.my_CkSpmTipoAmministrazione`,

            // Scheda coop edilizie
            `${formPaths.schedaCoopEdil}.my_CkAvviatoProgrammaRealizzazione`,
            `${formPaths.schedaCoopEdil}.my_ScEdilObbl`,
            `${formPaths.schedaCoopEdil}.my_CkIscrizAlbo`,
            `${formPaths.schedaCoopEdil}.my_numProgEdRealizz`,

            // Conclusioni
            `${formPaths.conclusioni}.my_DataCompilaz`,
        ]);
    }

    get centraleSoggDispostoRev$(): Observable<DbCentraleEntity | undefined> {
        const soggDispostoRevControl = getControl(this._form, `${formPaths.datiIdentificativi}.my_SoggDispostoRev`);
        return combineLatest([
            this.store.select(Revisione53DatiBusinessSelectors.getCentrale),
            soggDispostoRevControl.valueChanges.pipe(startWith(soggDispostoRevControl.value)),
        ]).pipe(map(([centrale, soggDispostoRev]) => centrale.find((c) => c.Id_cluster === soggDispostoRev)));
    }

    override initListener(): void {
        new ContribBiennaleFormListeners(this._form, this.store).setListeners();
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_dataUltimaSincronizzazione: '',
                my_dataCalcoloPenalita: '',
                my_mora: '',
                my_moraHidden: '',
                my_sanzione: '',
                my_sanzioneHidden: '',
                my_totaleMoraSanzione: '',
                my_TotaleDovutoAllaData: '',
                my_continuitaNelleRevisioni: '',
                my_IdAssegnazione: '',
                my_AllContributoAssociativo: this.fb.group({
                    my_AllElemContributoAssociativo: this.fb.array([]),
                }),
                my_AllContributoRevPrec: this.fb.group({
                    my_AllElemContributoRevPrec: this.fb.array([]),
                }),
                my_RTCalcoloMoraSanzione: this.fb.group({
                    my_RTRigaCalcoloMoraSanzione: this.fb.array([]),
                }),
                my_SezioneI__Rilevazione: this.fb.group({
                    my_Bilancio: this.fb.group({
                        my_CkEnteTenutoCertBil: '',
                        my_SocietaPartecipate: this.fb.group({
                            my_ElSocietaPartecipate: this.fb.array([]),
                        }),
                        my_ContoEconomico: this.fb.group({
                            my_CoEcA12: '',
                        }),
                    }),
                    my_AmministrazioneControllo: this.fb.group({
                        my_DataAssembleaLiquidazVolontaria: '',
                        my_AllLiquidatori: this.fb.group({
                            my_AllElemLiquidatori: this.fb.array([]),
                        }),
                        my_CkNorme: '',
                        my_NumLiquidatori: '',

                        my_NormeSrl: this.fb.group({
                            my_CkSrlCoop9soci: '',
                            my_CkSrlCoop20soci: '',
                            my_CkSrlAffidamContrCont: '',
                            my_CkSrlTipoAmministrazione: '',
                            my_SrlScadenzaAmministratori: '',
                        }),

                        my_NormeSpaDualistico: this.fb.group({
                            my_CkSpdAffidamContrCont: '',
                        }),

                        my_NormeSpaOrdinario: this.fb.group({
                            my_CkSpoAffidamContrCont: '',
                            my_CkSpoTipoAmministrazione: '',
                        }),

                        my_NormeSpaMonistico: this.fb.group({
                            my_CkSpmAffidamContrCont: '',
                            my_CkSpmTipoAmministrazione: '',
                        }),
                    }),
                    my_Conclusioni: this.fb.group({
                        my_DataCompilaz: '',
                    }),
                    my_SchedaCoopEdil: this.fb.group({
                        my_CkAvviatoProgrammaRealizzazione: '',
                        my_ScEdilObbl: '',
                        my_CkIscrizAlbo: '',
                        my_numProgEdRealizz: '',
                    }),
                    my_DatiIdentificativi: this.fb.group({
                        my_Anno1: '',
                        my_DenominazEnte: '',
                        my_data_immatricolazione_cci: '',
                        my_DataUltRev: '',
                        my_DataRevisioneDal: '',
                        my_SoggDispostoRev: '',
                        my_CkMutualPrev: '',
                        my_TipoSezEnte: '',
                        my_DataCostituz: '',
                        my_TipoIncarico: '',
                        my_TipoIncaricoEffettivo: '',
                        my_AderentiA: this.fb.group({
                            my_ElAderentiA: this.fb.array([]),
                        }),
                    }),
                    my_EffettivitaBaseSociale: this.fb.group({
                        my_ChiusuraEsercizio: '',
                        my_Data1EsPrec: '',
                        my_SociFisCoopPrimo: '',
                        my_SociGiuCoopPrimo: '',
                        my_SociFisFinPrimo: '',
                        my_SociGiuFinPrimo: '',
                        my_SociSpecPrimo: '',
                        my_Data2EsPrec: '',
                        my_SociFisCoopSecondo: '',
                        my_SociGiuCoopSecondo: '',
                        my_SociFisFinSecondo: '',
                        my_SociGiuFinSecondo: '',
                        my_SociSpecSecondo: '',
                    }),
                    my_DatiBilancio: this.fb.group({
                        my_BilAnno2: '',
                        my_BilAnno1: '',
                        my_StatoPatrimoniale__Passivo: this.fb.group({
                            my_StPaPsAI1: '',
                            my_StPaPsAI2: '',
                            my_StPaPsAtot1: '',
                            my_StPaPsAtot2: '',
                        }),

                        my_ContoEconomico: this.fb.group({
                            my_CoEcAtot1: '',
                            my_CoEcAtot2: '',
                        }),
                    }),
                    my_ContributoBiennale: this.fb.group({
                        my_ContribPreCaricato: '',
                        my_Anno1__1: '',
                        my_id_federazione: '',
                        my_id_biennio: ['', Validators.required],
                        my_Federazione: '',
                        my_SociInseriti: '',
                        my_CapitaleSociale: '',
                        my_RicaviVendPrest: '',
                        my_AumentoIspez: '',
                        my_ContributoDovutoModif: ['', Validators.required],
                        my_ContribDovuto: ['', Validators.required],
                        my_MotivoModificaContrib: [''],
                        my_ContribVersato: '',
                        my_MaggEntiEdilDovuta: '',
                        my_MaggEntiEdilVersata: '',
                        my_DataScadCalcPenal: '',
                        my_DataScadCalcPenalOrig: '',
                        my_NoteContrBienn: '',
                        my_CkContrBiennIrrUltRev: ['', Validators.required],
                        my_RichiedeDettagliContributoVersato: '',
                        my_CalcoloContributoBttn: [''],
                        my_ContoCorrentePrevistoPerVersamento: '',
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: contribBiennaleFormValidators,
            }
        );
    }

    protected override beforePopulateForm(myVerbale: MyVerbale) {
        buildLiquidatoriFormArray(this.fb, myVerbale, this._form);
        buildSocietaPartecipateFormArray(this.fb, myVerbale, this._form, false);
        buildAderentiAFormArray(this.fb, myVerbale, this._form, false);
        buildContribAssociativoFormArray(this.fb, myVerbale, this._form);
        buildContribRevPrecFormArray(this.fb, myVerbale, this._form);
        buildVersamentoFormArray(this.fb, myVerbale, this._form);
        setInitialTipoSezEnte(myVerbale, this.store);
    }

    protected override afterPopulateForm(myVerbale: MyVerbale): void {
        const my_Anno1 = toNum(myVerbale?.my_SezioneI__Rilevazione?.my_DatiIdentificativi?.my_Anno1);
        getContribBiennaleControl(this._form, 'my_Anno1__1').setValue((my_Anno1 - 1)?.toString(10));
    }
}
